import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Maximize, Bed, Bath, ShieldCheck, FileText, Share2, Heart, MessageSquare, Calendar } from 'lucide-react';
import { properties, formatNaira } from '../data/mockData';
import { VerificationBadge, TierBadge } from '../components/shared/Badges';
export const PropertyDetail = () => {
  const {
    id
  } = useParams();
  const property = properties.find(p => p.id === id) || properties[0]; // Fallback for demo
  return <div className="min-h-screen bg-neutral-light pt-24 pb-20" data-id="element-1872">
      <div className="container mx-auto px-4 md:px-6" data-id="element-1873">
        {/* Breadcrumb & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-1874">
          <div className="text-sm text-text-light flex items-center gap-2" data-id="element-1875">
            <Link to="/explore" className="hover:text-primary-900 transition-colors" data-id="element-1876">
              Explore
            </Link>
            <span data-id="element-1877">/</span>
            <span className="text-primary-900 font-medium" data-id="element-1878">
              {property.district}
            </span>
          </div>
          <div className="flex items-center gap-3" data-id="element-1879">
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm font-medium hover:bg-neutral-50 transition-colors" data-id="element-1880">
              <Share2 size={16} data-id="element-1881" /> Share
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 rounded-lg text-sm font-medium hover:bg-neutral-50 transition-colors" data-id="element-1882">
              <Heart size={16} data-id="element-1883" /> Save
            </button>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 h-[400px] md:h-[500px]" data-id="element-1884">
          <div className="md:col-span-3 h-full rounded-2xl overflow-hidden relative group" data-id="element-1885">
            <img src={property.images[0]} alt={property.title} className="w-full h-full object-cover" data-id="element-1886" />
            <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors" data-id="element-1887" />
          </div>
          <div className="hidden md:flex flex-col gap-4 h-full" data-id="element-1888">
            <img src={property.images[1] || property.images[0]} alt="Thumbnail" className="w-full h-1/2 object-cover rounded-2xl" data-id="element-1889" />
            <div className="w-full h-1/2 relative rounded-2xl overflow-hidden cursor-pointer group" data-id="element-1890">
              <img src={property.images[0]} alt="Thumbnail" className="w-full h-full object-cover" data-id="element-1891" />
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-black/40 transition-colors" data-id="element-1892">
                <span className="text-white font-medium" data-id="element-1893">+5 Photos</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-1894">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-1895">
            {/* Header Info */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-soft border border-neutral-200" data-id="element-1896">
              <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-1897">
                <TierBadge tier={property.tier as 1 | 2 | 3} data-id="element-1898" />
                {property.isVerified && <VerificationBadge status="Approved" data-id="element-1899" />}
                <span className="px-3 py-1 bg-neutral-100 text-text-light text-xs font-medium rounded-full" data-id="element-1900">
                  {property.type}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4 leading-tight" data-id="element-1901">
                {property.title}
              </h1>
              <div className="flex items-center text-text-light mb-6" data-id="element-1902">
                <MapPin size={18} className="mr-2 text-secondary" data-id="element-1903" />
                {property.district}, Abuja, FCT
              </div>
              <div className="text-3xl font-bold text-primary-900" data-id="element-1904">
                {formatNaira(property.price)}
              </div>
            </div>

            {/* Key Features */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-soft border border-neutral-200" data-id="element-1905">
              <h2 className="text-xl font-serif font-bold text-primary-900 mb-6" data-id="element-1906">
                Property Overview
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-1907">
                <div className="flex flex-col gap-1" data-id="element-1908">
                  <span className="text-text-light text-sm flex items-center gap-2" data-id="element-1909">
                    <Maximize size={16} data-id="element-1910" /> Size
                  </span>
                  <span className="font-semibold text-text" data-id="element-1911">
                    {property.size}
                  </span>
                </div>
                {property.bedrooms > 0 && <div className="flex flex-col gap-1" data-id="element-1912">
                    <span className="text-text-light text-sm flex items-center gap-2" data-id="element-1913">
                      <Bed size={16} data-id="element-1914" /> Bedrooms
                    </span>
                    <span className="font-semibold text-text" data-id="element-1915">
                      {property.bedrooms}
                    </span>
                  </div>}
                {property.bathrooms > 0 && <div className="flex flex-col gap-1" data-id="element-1916">
                    <span className="text-text-light text-sm flex items-center gap-2" data-id="element-1917">
                      <Bath size={16} data-id="element-1918" /> Bathrooms
                    </span>
                    <span className="font-semibold text-text" data-id="element-1919">
                      {property.bathrooms}
                    </span>
                  </div>}
                <div className="flex flex-col gap-1" data-id="element-1920">
                  <span className="text-text-light text-sm flex items-center gap-2" data-id="element-1921">
                    <FileText size={16} data-id="element-1922" /> Title
                  </span>
                  <span className="font-semibold text-text" data-id="element-1923">
                    {property.ownership}
                  </span>
                </div>
              </div>
            </div>

            {/* Verification Status */}
            <div className="bg-primary-50 p-6 md:p-8 rounded-2xl border border-primary-100" data-id="element-1924">
              <div className="flex items-center gap-3 mb-6" data-id="element-1925">
                <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center text-success" data-id="element-1926">
                  <ShieldCheck size={24} data-id="element-1927" />
                </div>
                <div data-id="element-1928">
                  <h2 className="text-xl font-serif font-bold text-primary-900" data-id="element-1929">
                    Verification Report
                  </h2>
                  <p className="text-sm text-text-light" data-id="element-1930">
                    Verified by AMU Compliance Team
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-1931">
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-neutral-200" data-id="element-1932">
                  <div className="flex items-center gap-3" data-id="element-1933">
                    <CheckCircle className="text-success" size={20} data-id="element-1934" />
                    <span className="font-medium text-text" data-id="element-1935">
                      Title Document (C of O)
                    </span>
                  </div>
                  <span className="text-xs font-bold text-success bg-success/10 px-2 py-1 rounded" data-id="element-1936">
                    VERIFIED
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-neutral-200" data-id="element-1937">
                  <div className="flex items-center gap-3" data-id="element-1938">
                    <CheckCircle className="text-success" size={20} data-id="element-1939" />
                    <span className="font-medium text-text" data-id="element-1940">
                      Seller Identity (KYC)
                    </span>
                  </div>
                  <span className="text-xs font-bold text-success bg-success/10 px-2 py-1 rounded" data-id="element-1941">
                    VERIFIED
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-neutral-200" data-id="element-1942">
                  <div className="flex items-center gap-3" data-id="element-1943">
                    <CheckCircle className="text-success" size={20} data-id="element-1944" />
                    <span className="font-medium text-text" data-id="element-1945">
                      AGIS/FCDA Record Match
                    </span>
                  </div>
                  <span className="text-xs font-bold text-success bg-success/10 px-2 py-1 rounded" data-id="element-1946">
                    VERIFIED
                  </span>
                </div>
              </div>
            </div>

            {/* Map Mock */}
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-soft border border-neutral-200" data-id="element-1947">
              <h2 className="text-xl font-serif font-bold text-primary-900 mb-6" data-id="element-1948">
                Location
              </h2>
              <div className="w-full h-[300px] bg-neutral-200 rounded-xl overflow-hidden relative" data-id="element-1949">
                <img src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=1000" alt="Map" className="w-full h-full object-cover opacity-60" data-id="element-1950" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-1951">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center text-white shadow-glow animate-pulse" data-id="element-1952">
                    <MapPin size={24} data-id="element-1953" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-1954">
            {/* Seller Card */}
            <div className="bg-white p-6 rounded-2xl shadow-soft border border-neutral-200 sticky top-24" data-id="element-1955">
              <h3 className="text-sm font-bold text-text-light uppercase tracking-wider mb-4" data-id="element-1956">
                Listed By
              </h3>

              <div className="flex items-center gap-4 mb-6" data-id="element-1957">
                <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center text-primary-900 font-serif font-bold text-2xl border border-primary-100" data-id="element-1958">
                  {property.seller.name.charAt(0)}
                </div>
                <div data-id="element-1959">
                  <h4 className="font-bold text-text text-lg" data-id="element-1960">
                    {property.seller.name}
                  </h4>
                  <div className="flex items-center gap-2 mt-1" data-id="element-1961">
                    <VerificationBadge status="Approved" type="seller" data-id="element-1962" />
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6" data-id="element-1963">
                <button className="w-full bg-primary-900 hover:bg-primary-800 text-white py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2" data-id="element-1964">
                  <Calendar size={18} data-id="element-1965" /> Book Inspection
                </button>
                <button className="w-full bg-white border-2 border-primary-900 text-primary-900 hover:bg-primary-50 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2" data-id="element-1966">
                  <MessageSquare size={18} data-id="element-1967" /> Message Seller
                </button>
              </div>

              <div className="p-4 bg-neutral-50 rounded-xl text-sm text-text-light border border-neutral-100 text-center" data-id="element-1968">
                Transactions are secure. Never pay outside the AMU platform
                before an inspection.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
const CheckCircle = ({
  className,
  size
}: {
  className?: string;
  size?: number;
}) => <svg xmlns="http://www.w3.org/2000/svg" width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-1969">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" data-id="element-1970"></path>
    <polyline points="22 4 12 14.01 9 11.01" data-id="element-1971"></polyline>
  </svg>;